import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { navigate } from '../App'
import { colors } from '../theme'
import { getDashboardMetrics, type MetricCard } from '../api/sequences'

type Scope = 'mine' | 'all'

export default function Dashboard() {
  const { authHeaders, apiBase, user } = useAuth()
  const [scope, setScope] = useState<Scope>('mine')
  const [cards, setCards] = useState<MetricCard[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setCards(null)
    setError(null)
    getDashboardMetrics(apiBase, authHeaders(), scope)
      .then((r) => !cancelled && setCards(r.cards))
      .catch(
        (e: unknown) =>
          !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'),
      )
    return () => {
      cancelled = true
    }
  }, [apiBase, authHeaders, scope])

  // Group cards by `.group` while preserving registry order from the
  // backend. The metric registry returns cards in declaration order so
  // groups appear in a predictable layout (Proposals first, then
  // Outreach, then whatever else gets registered later).
  const grouped = useMemo(() => {
    if (!cards) return [] as Array<{ group: string; cards: MetricCard[] }>
    const order: string[] = []
    const map = new Map<string, MetricCard[]>()
    for (const c of cards) {
      if (!map.has(c.group)) {
        map.set(c.group, [])
        order.push(c.group)
      }
      map.get(c.group)!.push(c)
    }
    return order.map((g) => ({ group: g, cards: map.get(g)! }))
  }, [cards])

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 16,
          marginBottom: 24,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '2.5px',
              color: colors.purple,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            {scope === 'mine'
              ? `Signed in as ${user?.email}`
              : 'All Parkify reps'}
          </div>
          <h1
            style={{
              fontSize: 32,
              fontWeight: 700,
              letterSpacing: '-1px',
              margin: 0,
              color: colors.black,
            }}
          >
            Sales Dashboard
          </h1>
          <p
            style={{
              color: colors.textMuted,
              fontSize: 14,
              marginTop: 6,
              lineHeight: 1.55,
              maxWidth: 640,
            }}
          >
            Pipeline health at a glance. Cards update as the dispatcher
            ticks (every 10 min) and the reply detector sweeps (every 5
            min). New tools that land on the Sales Portal can register
            their own metrics — they show up here automatically.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ScopeToggle scope={scope} onChange={setScope} />
          <button
            onClick={() => navigate({ name: 'sequences' })}
            style={btnGhost}
          >
            View sequences
          </button>
          <button
            onClick={() => navigate({ name: 'new' })}
            style={btnPrimary}
          >
            + New proposal
          </button>
        </div>
      </div>

      {error ? (
        <ErrorBlock message={error} />
      ) : cards === null ? (
        <Skeleton />
      ) : cards.length === 0 ? (
        <Empty />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {grouped.map((g) => (
            <GroupBlock key={g.group} group={g.group} cards={g.cards} />
          ))}
        </div>
      )}
    </div>
  )
}

function ScopeToggle({
  scope,
  onChange,
}: {
  scope: Scope
  onChange: (s: Scope) => void
}) {
  return (
    <div
      style={{
        display: 'inline-flex',
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 999,
        padding: 3,
      }}
    >
      {(['mine', 'all'] as Scope[]).map((s) => (
        <button
          key={s}
          onClick={() => onChange(s)}
          style={{
            background: scope === s ? colors.purple : 'transparent',
            color: scope === s ? colors.white : colors.textMuted,
            border: 'none',
            borderRadius: 999,
            padding: '6px 14px',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          {s === 'mine' ? 'My pipeline' : 'All reps'}
        </button>
      ))}
    </div>
  )
}

function GroupBlock({ group, cards }: { group: string; cards: MetricCard[] }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '2.5px',
          color: colors.purple,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        {group}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 12,
        }}
      >
        {cards.map((c) => (
          <Card key={c.name} card={c} />
        ))}
      </div>
    </div>
  )
}

function Card({ card }: { card: MetricCard }) {
  return (
    <div
      style={{
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 12,
        padding: '16px 18px',
        boxShadow: '0 2px 8px rgba(35,31,32,0.04)',
        minHeight: 110,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '1.6px',
          color: colors.purple,
          textTransform: 'uppercase',
        }}
      >
        {card.label}
      </div>
      <div
        style={{
          fontSize: 30,
          fontWeight: 700,
          letterSpacing: '-1px',
          color: colors.black,
          lineHeight: 1.1,
          marginTop: 6,
        }}
      >
        {card.value}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 6,
        }}
      >
        {card.sublabel ? (
          <span style={{ fontSize: 12, color: colors.textMuted }}>
            {card.sublabel}
          </span>
        ) : null}
        {card.trend ? (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: card.trend.startsWith('-') ? colors.red : colors.purple,
              letterSpacing: '0.5px',
            }}
          >
            {card.trend}
          </span>
        ) : null}
      </div>
    </div>
  )
}

function Skeleton() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: 12,
      }}
    >
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          style={{
            border: `1px solid ${colors.divider}`,
            borderRadius: 12,
            minHeight: 110,
            background:
              'linear-gradient(90deg, rgba(255,255,255,0), rgba(127,63,152,0.06), rgba(255,255,255,0))',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.4s linear infinite',
          }}
        />
      ))}
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
    </div>
  )
}

function Empty() {
  return (
    <div
      style={{
        background: colors.white,
        border: `1px dashed ${colors.gray}`,
        borderRadius: 12,
        padding: 30,
        color: colors.textMuted,
        textAlign: 'center',
      }}
    >
      No metrics registered yet.
    </div>
  )
}

function ErrorBlock({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: '12px 16px',
        background: colors.warningBg,
        border: `1.5px solid ${colors.warningBorder}`,
        borderRadius: 10,
        fontSize: 13,
        color: colors.black,
      }}
    >
      <strong style={{ color: colors.red }}>Error:</strong> {message}
    </div>
  )
}

const btnPrimary: React.CSSProperties = {
  background: colors.purple,
  color: colors.white,
  border: 'none',
  borderRadius: 8,
  padding: '8px 16px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  cursor: 'pointer',
}
const btnGhost: React.CSSProperties = {
  background: colors.white,
  color: colors.black,
  border: `1px solid ${colors.divider}`,
  borderRadius: 8,
  padding: '8px 14px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  cursor: 'pointer',
}
